import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@lichtblick/apollo';
import { useTranslation } from '@lichtblick/i18next';
import { logger } from '@lichtblick/logger';
import {
  Query,
  QueryGetBranchOffices_V1Args,
  AppointmentTypeSelection,
  AppointmentTypeSelectionNullObject,
  Option,
  GetTeams_V1Query,
  GetTeams_V1QueryVariables,
  GetUsers_V1Query,
  GetUsers_V1QueryVariables,
  GetCraftCompanies_V1Query,
  QueryGetCraftCompanies_V1Args,
  GetWorkOrders_V1Query,
  GetWorkflowDefinitionsComposite_V1Query,
  QueryGetWorkflowDefinitionVersion_V1Args,
  ApprovalState,
  GetWorkCategories_V1Query,
  QueryGetWorkCategories_V1Args,
} from '@lichtblick/types';
import { DEFAULT_PAGE_SIZE, useLogoutOnApolloError } from '@lichtblick/utils';

import { GET_CRAFT_COMPANIES, GET_USERS } from '../../graphql/pm/appointment';
import { GET_BRANCH_FILTER_OPTIONS } from '../../graphql/pm/branch';
import { GET_TEAMS_FILTER_OPTIONS } from '../../graphql/pm/team';
import { GET_WORKFLOW_DEFINITIONS_COMPOSITE } from '../../graphql/pm/workflowDefinitions';
import { GET_ORDERS } from '../../graphql/qm/order';
import { GET_WORK_CATEGORIES } from '../../graphql/qm/order-categories';

const productTypes: Option[] = [
  {
    label: 'Other',
    value: 1,
  },
  {
    label: 'Solar',
    value: 2,
  },
  {
    label: 'Heat',
    value: 3,
  },
  {
    label: 'Solar Light',
    value: 5491,
  },
  {
    label: 'Solar String',
    value: 5492,
  },
  {
    label: 'Wallet Only',
    value: 5493,
  },
];

export const useOptions = () => {
  const { t } = useTranslation();

  const [branchQuery, setBranchQuery] = useState<string>('');
  const [filteredBranchOptions, setFilteredBranchOptions] = useState<Option[]>([]);
  const [teamQuery, setTeamQuery] = useState<string>('');
  const [filteredTeamOptions, setFilteredTeamOptions] = useState<Option[]>([]);
  const [appointmentTypeQuery, setAppointmentTypeQuery] = useState<string>('');
  const [appointmentTypeOptions, setAppointmentTypeOptions] = useState<Option<AppointmentTypeSelection>[]>([]);
  const [workCategoryQuery, setWorkCategoryQuery] = useState<string>('');
  const [filteredWorkCategoryOptions, setWorkCategoryOptions] = useState<Option[]>([]);
  const [craftmanQuery, setCraftmanQuery] = useState<string>('');
  const [craftmanListOptions, setCraftmanListOptions] = useState<Option[]>([]);
  const [qualityManagerQuery, setQualityManagerQuery] = useState<string>('');
  const [filteredQualityManagerOptions, setQualityManagerOptions] = useState<Option[]>([]);
  const [partnerQuery, setPartnerQuery] = useState<string>('');
  const [filteredPartnerOptions, setPartnerListOptions] = useState<Option[]>([]);
  const [workOrderQuery, setWorkOrderQuery] = useState<string>('');
  const [filteredWorkOrderOptions, setWorkOrderOptions] = useState<Option[]>([]);
  const [productTypeQuery, setProductTypeQuery] = useState<string>('');
  const [filteredProductTypeOptions, setProductTypeOptions] = useState<Option[]>([]);
  const [approvalStateQuery, setApprovalStateQuery] = useState<string>('');
  const [filteredApprovalStateOptions, setFilteredApprovalStateOptions] = useState<Option[]>([]);

  const { data: qualityManagerOptions, error: qualityManagerError } = useQuery<
    GetUsers_V1Query,
    GetUsers_V1QueryVariables
  >(GET_USERS, {
    variables: {
      roleId: 4,
      search: qualityManagerQuery.length > 2 ? qualityManagerQuery : null,
      page: { number: 0, size: DEFAULT_PAGE_SIZE },
    },
  });

  const { data: branchOptions, error: branchOptionsError } = useQuery<Query, QueryGetBranchOffices_V1Args>(
    GET_BRANCH_FILTER_OPTIONS,
    {
      variables: { input: { includeDeleted: true, page: { number: 0, size: DEFAULT_PAGE_SIZE } } },
    },
  );
  const { data: teamOptions, error: teamOptionsError } = useQuery<GetTeams_V1Query, GetTeams_V1QueryVariables>(
    GET_TEAMS_FILTER_OPTIONS,
    {
      variables: { input: { includeDeleted: true, page: { number: 0, size: DEFAULT_PAGE_SIZE } } },
    },
  );

  const { data: workOrderOptions, error: workOrderOptionsError } = useQuery<GetWorkOrders_V1Query>(GET_ORDERS, {
    variables: { page: { number: 0, size: DEFAULT_PAGE_SIZE }, search: workOrderQuery, onlyWithAppointments: false },
    fetchPolicy: 'no-cache',
  });

  const { data: personOptions, error: personOptionsError } = useQuery<GetUsers_V1Query, GetUsers_V1QueryVariables>(
    GET_USERS,
    {
      variables: {
        roleId: 3,
        search: craftmanQuery.length > 2 ? craftmanQuery : null,
        page: { number: 0, size: DEFAULT_PAGE_SIZE },
      },
    },
  );

  const { data: workflowDefinitionsComposite, error: workflowDefinitionsCompositeError } = useQuery<
    GetWorkflowDefinitionsComposite_V1Query,
    QueryGetWorkflowDefinitionVersion_V1Args
  >(GET_WORKFLOW_DEFINITIONS_COMPOSITE, {});

  const { data: craftCompanies } = useQuery<GetCraftCompanies_V1Query, QueryGetCraftCompanies_V1Args>(
    GET_CRAFT_COMPANIES,
    {
      variables: {
        input: {
          page: { number: 0, size: DEFAULT_PAGE_SIZE },
          search: partnerQuery.length > 2 ? partnerQuery : null,
        },
      },
    },
  );

  const { data: workCategories, error: workCategoriesError } = useQuery<
    GetWorkCategories_V1Query,
    QueryGetWorkCategories_V1Args
  >(GET_WORK_CATEGORIES, {
    variables: {
      input: {
        distinctByName: true,
      },
    },
  });

  useLogoutOnApolloError(qualityManagerError);
  useLogoutOnApolloError(branchOptionsError);
  useLogoutOnApolloError(teamOptionsError);
  useLogoutOnApolloError(workOrderOptionsError);
  useLogoutOnApolloError(personOptionsError);
  useLogoutOnApolloError(workflowDefinitionsCompositeError);
  useLogoutOnApolloError(workCategoriesError);

  const defaultOpt = useMemo(() => ({ label: t('pm:filters.all'), value: undefined }), [t]);

  useEffect(() => {
    let options: Option[] | undefined = branchOptions?.getBranchOffices_V1?.data?.map((branch) => ({
      isDeleted: branch.isDeleted,
      label: branch.name ?? '',
      value: branch.id,
    }));

    if (!options) {
      logger.debug('useOptions branch option data unavailable', {
        component: 'useOptions',
        branchQueryLength: branchQuery.length,
      });

      return;
    }

    options = [defaultOpt, ...options];

    if (branchQuery.length > 0) {
      setFilteredBranchOptions(options.filter(({ label }) => label?.toLowerCase().includes(branchQuery.toLowerCase())));
    } else {
      setFilteredBranchOptions(options);
    }

    logger.debug('useOptions transformed branch options', {
      component: 'useOptions',
      branchCount: options.length,
      branchQueryLength: branchQuery.length,
    });
  }, [branchQuery, branchOptions, defaultOpt]);

  useEffect(() => {
    const teamOptionsFormatted = teamOptions?.getTeams_V1?.data
      ? [
          ...(teamOptions?.getTeams_V1?.data.map((team) => ({
            isDeleted: team.isDeleted,
            label: `${team.name} - ${team.branchOffice?.name ?? ''}`,
            value: team.id,
          })) ?? []),
        ].sort((a, b) => a.label.localeCompare(b.label))
      : [];

    if (teamQuery.length > 0) {
      setFilteredTeamOptions(
        teamOptionsFormatted.filter(({ label }) => label?.toLowerCase().includes(teamQuery.toLowerCase())),
      );
    } else {
      setFilteredTeamOptions(teamOptionsFormatted);
    }

    logger.debug('useOptions transformed team options', {
      component: 'useOptions',
      teamCount: teamOptionsFormatted.length,
      teamQueryLength: teamQuery.length,
    });
  }, [teamQuery, teamOptions]);

  useEffect(() => {
    const appointmentTypeOptionsFormatted = workflowDefinitionsComposite?.getWorkflowDefinitionsComposite_V1?.data
      ? [
          ...(workflowDefinitionsComposite?.getWorkflowDefinitionsComposite_V1?.data.appointmentTypes?.map((def) => ({
            label: def.name ?? '',
            value: { id: def.id, kind: 'appointment' } as AppointmentTypeSelection,
          })) ?? []),
        ]
      : [];
    const blockerOptionsFormatted = workflowDefinitionsComposite?.getWorkflowDefinitionsComposite_V1?.data
      ? [
          ...(workflowDefinitionsComposite?.getWorkflowDefinitionsComposite_V1?.data.blockerReasons?.map((def) => ({
            label: def.title ?? '',
            value: { id: def.id, kind: 'blocker' } as AppointmentTypeSelection,
          })) ?? []),
        ]
      : [];

    const typeOptionsFormatted = appointmentTypeOptionsFormatted
      .concat(blockerOptionsFormatted)
      .sort((a, b) => a.label.localeCompare(b.label));

    if (appointmentTypeQuery.length > 0) {
      setAppointmentTypeOptions(
        typeOptionsFormatted.filter(({ label }) => label?.toLowerCase().includes(appointmentTypeQuery.toLowerCase())),
      );
    } else {
      setAppointmentTypeOptions(typeOptionsFormatted);
    }

    logger.debug('useOptions transformed appointment type options', {
      component: 'useOptions',
      appointmentTypeCount: appointmentTypeOptionsFormatted.length,
      blockerCount: blockerOptionsFormatted.length,
      queryLength: appointmentTypeQuery.length,
    });
  }, [appointmentTypeQuery, workflowDefinitionsComposite]);

  useEffect(() => {
    const options: Option[] | undefined = personOptions?.getUsers_V1?.data
      ? [
          ...(personOptions?.getUsers_V1?.data?.map((user) => ({
            isDeleted: user.isDeleted,
            label: `${user.firstName} ${user.lastName} (${user.eMail})`,
            value: user.id,
          })) ?? []),
        ].sort((a, b) => a.label.localeCompare(b.label))
      : [];

    setCraftmanListOptions(options ?? []);

    logger.debug('useOptions transformed craftsmen options', {
      component: 'useOptions',
      craftsmanCount: options.length,
    });
  }, [personOptions]);

  useEffect(() => {
    const options: Option[] | undefined = qualityManagerOptions?.getUsers_V1?.data
      ? [
          ...(qualityManagerOptions?.getUsers_V1?.data?.map((user) => ({
            isDeleted: user.isDeleted,
            label: `${user.firstName} ${user.lastName}`,
            value: user.id,
          })) ?? []),
        ]
      : [];

    setQualityManagerOptions(options ?? []);
  }, [qualityManagerOptions]);

  useEffect(() => {
    const partnerCompanyOptionsFormatted = craftCompanies?.getCraftCompanies_V1.data
      ? [
          ...(craftCompanies?.getCraftCompanies_V1?.data.map((def) => ({
            label: def.name ?? '',
            value: def.id,
          })) ?? []),
        ].sort((a, b) => a.label.localeCompare(b.label))
      : [];

    setPartnerListOptions(partnerCompanyOptionsFormatted);
  }, [craftCompanies]);

  useEffect(() => {
    const workOrdersFormatted = workOrderOptions?.getWorkOrders_V1.data
      ? [
          ...(workOrderOptions?.getWorkOrders_V1?.data.map((order) => ({
            label: order.name ?? '',
            value: order.id,
          })) ?? []),
        ]
      : [];

    setWorkOrderOptions(workOrdersFormatted);

    logger.debug('useOptions transformed work order options', {
      component: 'useOptions',
      workOrderCount: workOrdersFormatted.length,
    });
  }, [workOrderOptions]);

  useEffect(() => {
    const options = [defaultOpt, ...productTypes];

    if (productTypeQuery.length > 0) {
      setProductTypeOptions(
        options.filter(({ label }) => label?.toLowerCase().includes(productTypeQuery.toLowerCase())),
      );
    } else {
      setProductTypeOptions(options);
    }
  }, [defaultOpt, productTypeQuery]);

  useEffect(() => {
    let options: Option[] | undefined = workCategories?.getWorkCategories_V1?.data?.map((cat) => ({
      label: cat.name ?? '',
      value: cat.name ?? '',
    }));

    if (!options) {
      return;
    }

    options = [defaultOpt, ...options];

    if (workCategoryQuery.length > 0) {
      setWorkCategoryOptions(
        options.filter(({ label }) => label?.toLowerCase().includes(workCategoryQuery.toLowerCase())),
      );
    } else {
      setWorkCategoryOptions(options);
    }

    logger.debug('useOptions transformed work category options', {
      component: 'useOptions',
      workCategoryCount: options.length,
      workCategoryQueryLength: workCategoryQuery.length,
    });
  }, [workCategories, workCategoryQuery, defaultOpt]);

  useEffect(() => {
    const approvalStateOptions: Option[] = Object.values(ApprovalState).map((state) => ({
      label: t(`glossary:workStatus.${state.toLowerCase()}`),
      value: state,
    }));

    const allOptions = [defaultOpt, ...approvalStateOptions];

    if (approvalStateQuery.length > 0) {
      setFilteredApprovalStateOptions([
        defaultOpt,
        ...allOptions.filter(({ label }) => label?.toLowerCase().includes(approvalStateQuery.toLowerCase())),
      ]);
    } else {
      setFilteredApprovalStateOptions([defaultOpt, ...allOptions]);
    }
  }, [approvalStateQuery, defaultOpt, t]);

  const normalizeAppointmentTypeSelection = (type: AppointmentTypeSelection | null | undefined) => {
    if (type && typeof type === 'object') {
      return type;
    }

    return AppointmentTypeSelectionNullObject;
  };

  const checkIsNotTypes = (type: AppointmentTypeSelection | null | undefined, isNotTypes: string[]) => {
    const normalizedType = normalizeAppointmentTypeSelection(type);

    if (normalizedType.kind === 'appointment') {
      const isNotIdsAppointment =
        workflowDefinitionsComposite?.getWorkflowDefinitionsComposite_V1.data?.appointmentTypes
          ?.filter((el) => (el.name ? isNotTypes.includes(el.name) : false))
          .map((el) => el.id);

      return !isNotIdsAppointment?.includes(normalizedType.id);
    }

    if (normalizedType.kind === 'blocker') {
      const isNotIdsBlocker = workflowDefinitionsComposite?.getWorkflowDefinitionsComposite_V1.data?.blockerReasons
        ?.filter((el) => (el.title ? isNotTypes.includes(el.title) : false))
        .map((el) => el.id);

      return !isNotIdsBlocker?.includes(normalizedType.id);
    }

    return false;
  };

  const checkIsType = (type: AppointmentTypeSelection | null | undefined, isTypes: string[]) => {
    const normalizedType = normalizeAppointmentTypeSelection(type);

    const isIdsAppointment = workflowDefinitionsComposite?.getWorkflowDefinitionsComposite_V1.data?.appointmentTypes
      ?.filter((el) => (el.name ? isTypes.includes(el.name) : false))
      .map((el) => el.id);

    const isIdsBlocker = workflowDefinitionsComposite?.getWorkflowDefinitionsComposite_V1.data?.blockerReasons
      ?.filter((el) => (el.title ? isTypes.includes(el.title) : false))
      .map((el) => el.id);

    if (normalizedType.kind === 'appointment') {
      return isIdsAppointment?.includes(normalizedType.id) ?? false;
    }

    if (normalizedType.kind === 'blocker') {
      return isIdsBlocker?.includes(normalizedType.id) ?? false;
    }

    return false;
  };

  const checkBlocker = (type: AppointmentTypeSelection | null | undefined | string) => {
    if (!type) {
      return false;
    }

    if (typeof type === 'string') {
      const appointmentItem =
        workflowDefinitionsComposite?.getWorkflowDefinitionsComposite_V1.data?.appointmentTypes?.find(
          (appo) => appo.name?.toLocaleLowerCase() === type.toLocaleLowerCase(),
        );

      return !appointmentItem;
    }

    return type.kind === 'blocker';
  };

  return {
    filteredBranchOptions,
    setBranchQuery,
    defaultOpt,
    setTeamQuery,
    filteredTeamOptions,
    appointmentTypeOptions,
    setAppointmentTypeQuery,
    setCraftmanQuery,
    craftmanListOptions,
    setPartnerQuery,
    filteredPartnerOptions,
    setWorkOrderQuery,
    filteredWorkOrderOptions,
    setProductTypeQuery,
    filteredProductTypeOptions,
    setQualityManagerQuery,
    filteredQualityManagerOptions,
    setApprovalStateQuery,
    filteredApprovalStateOptions,
    filteredWorkCategoryOptions,
    setWorkCategoryQuery,
    checkIsType,
    checkIsNotTypes,
    checkBlocker,
  };
};
